import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { ArrowLeft } from "@phosphor-icons/react";

const EMPTY = {
    name: "", code: "", dob: "", sex: "", weight_kg: "", height_cm: "",
    activity_level: "", weight_goal: "",
    objectives_text: "", aesthetic_treatments_history_text: "",
    current_supplements_text: "", allergies_text: "",
    medical_notes: "", epigenetics_notes: "",
};

const ACTIVITY = ["", "sedentario", "moderado", "activo", "atleta"];
const SEX = ["", "M", "F", "otro"];
const WEIGHT_GOAL = ["", "mantener", "bajar", "subir"];

function toArr(s) { return (s || "").split(",").map((x) => x.trim()).filter(Boolean); }
function fromArr(a) { return (a || []).join(", "); }

export default function PatientForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const editing = !!id;
    const [form, setForm] = useState(EMPTY);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (editing) {
            api.get(`/patients/${id}`).then((r) => {
                const d = r.data;
                setForm({
                    ...EMPTY,
                    ...d,
                    weight_kg: d.weight_kg ?? "",
                    height_cm: d.height_cm ?? "",
                    objectives_text: fromArr(d.objectives),
                    aesthetic_treatments_history_text: fromArr(d.aesthetic_treatments_history),
                    current_supplements_text: fromArr(d.current_supplements),
                    allergies_text: fromArr(d.allergies),
                });
            });
        }
    }, [id, editing]);

    const upd = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        const payload = {
            name: form.name,
            code: form.code || null,
            dob: form.dob || null,
            sex: form.sex || null,
            weight_kg: form.weight_kg ? Number(form.weight_kg) : null,
            height_cm: form.height_cm ? Number(form.height_cm) : null,
            activity_level: form.activity_level || null,
            weight_goal: form.weight_goal || null,
            objectives: toArr(form.objectives_text),
            aesthetic_treatments_history: toArr(form.aesthetic_treatments_history_text),
            current_supplements: toArr(form.current_supplements_text),
            allergies: toArr(form.allergies_text),
            medical_notes: form.medical_notes || null,
            epigenetics_notes: form.epigenetics_notes || null,
        };
        try {
            const r = editing
                ? await api.patch(`/patients/${id}`, payload)
                : await api.post("/patients", payload);
            toast.success(editing ? "Paciente actualizado" : "Paciente creado");
            navigate(`/pacientes/${r.data.id}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Error al guardar");
        } finally { setSubmitting(false); }
    };

    return (
        <div className="fade-up">
            <PageHeader
                eyebrow={editing ? "Editar paciente" : "Nuevo paciente"}
                title={editing ? form.name || "Editar paciente" : "Añadir paciente"}
                lead="Toda la información clínica y de objetivos del paciente para personalizar las fórmulas."
            >
                <div className="mt-4">
                    <Link to="/pacientes" className="text-sm text-[var(--ink-2)] inline-flex items-center gap-1 hover:text-[var(--ink)]">
                        <ArrowLeft size={14}/> Volver
                    </Link>
                </div>
            </PageHeader>

            <form onSubmit={submit} className="px-8 md:px-12 py-8 max-w-5xl">
                <div className="grid lg:grid-cols-2 gap-6">
                    <Section title="Identificación">
                        <F label="Nombre *"><input required className="i" value={form.name} onChange={upd("name")} data-testid="patient-name-input"/></F>
                        <F label="Código interno"><input className="i" value={form.code} onChange={upd("code")} data-testid="patient-code-input"/></F>
                        <F label="Fecha de nacimiento (YYYY-MM-DD)"><input type="date" className="i" value={form.dob} onChange={upd("dob")} data-testid="patient-dob-input"/></F>
                        <F label="Sexo">
                            <select className="i" value={form.sex} onChange={upd("sex")} data-testid="patient-sex-select">
                                {SEX.map((s) => <option key={s} value={s}>{s || "—"}</option>)}
                            </select>
                        </F>
                    </Section>

                    <Section title="Antropometría y actividad">
                        <F label="Peso (kg)"><input type="number" step="0.1" className="i" value={form.weight_kg} onChange={upd("weight_kg")} data-testid="patient-weight-input"/></F>
                        <F label="Altura (cm)"><input type="number" className="i" value={form.height_cm} onChange={upd("height_cm")} data-testid="patient-height-input"/></F>
                        <F label="Nivel de actividad">
                            <select className="i" value={form.activity_level} onChange={upd("activity_level")} data-testid="patient-activity-select">
                                {ACTIVITY.map((a) => <option key={a} value={a}>{a || "—"}</option>)}
                            </select>
                        </F>
                        <F label="Objetivo de peso">
                            <select className="i" value={form.weight_goal} onChange={upd("weight_goal")} data-testid="patient-weightgoal-select">
                                {WEIGHT_GOAL.map((a) => <option key={a} value={a}>{a || "—"}</option>)}
                            </select>
                        </F>
                    </Section>

                    <Section title="Objetivos y antecedentes">
                        <F label="Objetivos terapéuticos (separados por coma)"><input className="i" value={form.objectives_text} onChange={upd("objectives_text")} placeholder="recuperación piel, antienvejecimiento, sueño" data-testid="patient-objectives-input"/></F>
                        <F label="Historial de tratamientos estéticos (coma)"><input className="i" value={form.aesthetic_treatments_history_text} onChange={upd("aesthetic_treatments_history_text")} placeholder="ácido hialurónico, láser CO2" data-testid="patient-history-input"/></F>
                        <F label="Suplementos actuales (coma)"><input className="i" value={form.current_supplements_text} onChange={upd("current_supplements_text")} placeholder="vitamina D3, omega-3" data-testid="patient-supplements-input"/></F>
                        <F label="Alergias (coma)"><input className="i" value={form.allergies_text} onChange={upd("allergies_text")} placeholder="frutos secos, marisco" data-testid="patient-allergies-input"/></F>
                    </Section>

                    <Section title="Notas">
                        <F label="Notas médicas"><textarea rows={4} className="i resize-none" value={form.medical_notes} onChange={upd("medical_notes")} data-testid="patient-medicalnotes-input"/></F>
                        <F label="Epigenética / observaciones"><textarea rows={4} className="i resize-none" value={form.epigenetics_notes} onChange={upd("epigenetics_notes")} data-testid="patient-epigenetics-input"/></F>
                    </Section>
                </div>

                <div className="mt-8 border-t hairline pt-6 flex justify-end">
                    <button type="submit" disabled={submitting} className="btn-primary" data-testid="patient-submit-button">
                        {submitting ? "Guardando…" : editing ? "Guardar cambios" : "Crear paciente"}
                    </button>
                </div>
            </form>

            <style>{`.i{width:100%;padding:9px 12px;border:1px solid var(--line);background:#fff;font-size:.875rem;outline:none}.i:focus{border-color:var(--sage)}`}</style>
        </div>
    );
}

function Section({ title, children }) {
    return (<div className="border hairline bg-white p-6 space-y-4"><div className="label-eyebrow">{title}</div>{children}</div>);
}
function F({ label, children }) {
    return (<div><label className="text-xs text-[var(--ink-2)] block mb-1.5">{label}</label>{children}</div>);
}
